/* The rail that introduces the path before anything is opened: what the page
   is, how much of it there is, how to read the map, and the single next action.
 */
import { useTranslation } from 'react-i18next';
import { bonusNode, chapterDone, core, doneBlocks, firstOpen, optionalStages, pad, totalBlocks } from '../../engine/progress';
import { useApp } from '../../state/AppProvider';
import type { BlockKind } from '../../types';

const LEGEND_KINDS: BlockKind[] = ['explain', 'film', 'options', 'math', 'tree', 'check'];

export function Overview() {
  const { t } = useTranslation();
  const { progress, selectNode, resetConfirm } = useApp();

  const chapters = core();
  const extras = optionalStages();
  const bonus = bonusNode();
  const doneChapters = chapters.filter((n) => chapterDone(n, progress)).length;
  const doneExtras = extras.filter((n) => chapterDone(n, progress)).length;
  const next = firstOpen(progress);
  const started = doneBlocks(progress) > 0;

  return (
    <div className="panel-body" data-od-id="overview-rail">
      <div className="rail-main">
        <p className="eyebrow">{t('overviewEyebrow')}</p>
        <h1 className="h1 rail-title">{t('overviewTitle')}</h1>
        <p className="lead rail-lead">{t('overviewLead')}</p>
      </div>

      <div className="rail-stats">
        <div className="stat-pair">
          <div>
            <div className="stat-num num">{totalBlocks()}</div>
            <p className="stat-label">{t('statBlocks')}</p>
          </div>
          {extras.length > 0 && (
            <div>
              <div className="stat-num num">
                {doneExtras}/{extras.length}
              </div>
              <p className="stat-label">{t('statDepth')}</p>
            </div>
          )}
        </div>

        <div className="card-flat">
          <p className="eyebrow" style={{ marginBottom: 10 }}>
            {t('bmapTitle')}
          </p>
          <div className="legend">
            <span>
              <i className="swatch locked" />
              {t('routes:legendLater')}
            </span>
            <span>
              <i className="swatch available" />
              {t('legendAvailable')}
            </span>
            <span>
              <i className="swatch next" />
              {t('legendNext')}
            </span>
            <span>
              <i className="swatch done" />
              {t('legendDone')}
              <em className="legend-n num">
                {doneChapters}/{chapters.length}
              </em>
            </span>
            <span>
              <i className="swatch extra" />
              {t('legendExtra')}
            </span>
            {bonus && (
              <span>
                <i className="swatch bonus" />
                {t('legendBonus')}
              </span>
            )}
          </div>
          <div className="bmap-legend">
            {LEGEND_KINDS.map((kind) => (
              <span key={kind} className="bm-item">
                <i className="pip" data-kind={kind} data-s="done" />
                {t(`kind.${kind}`)}
              </span>
            ))}
          </div>
          <p className="meta" style={{ marginTop: 9 }}>
            {t('bmapLead')}
          </p>
        </div>
      </div>

      <div className="rail-act">
        <button
          type="button"
          className="btn btn-primary arrow btn-block"
          data-act="node"
          data-id={next.id}
          data-od-id="rail-cta"
          onClick={() => selectNode(next.id)}
        >
          {started ? `${t('ctaContinue')} · ${pad(next)}` : t('ctaStart')}
        </button>
        {bonus && (
          <button
            type="button"
            className="btn btn-secondary btn-block"
            data-act="node"
            data-id={bonus.id}
            data-od-id="rail-bonus"
            onClick={() => selectNode(bonus.id)}
          >
            {t('bonusCta')}
          </button>
        )}
        {started && (
          <button type="button" className="btn btn-ghost btn-block" data-act="reset" onClick={resetConfirm.ask}>
            {t('reset')}
          </button>
        )}
      </div>
    </div>
  );
}
